/**
 * A cheap answer to "can this browser save what I am editing?", asked once on
 * load so the warning appears before someone spends twenty minutes on an edit
 * rather than at the end of it.
 *
 * The authoritative negotiation happens per export in codecs.ts, against the
 * real output size. This probe uses 1280x720 as a stand-in, which is enough to
 * tell an encoder that exists from one that does not.
 */

import { negotiate, supportNote } from "./codecs.ts";

export interface Capabilities {
  video: boolean;
  audio: boolean;
  /** What the export will actually use, e.g. "H.264 main". */
  label: string | null;
  /** The caveat to show, if any. */
  note: string | null;
  checked: boolean;
}

export const UNKNOWN_CAPABILITIES: Capabilities = {
  video: false,
  audio: false,
  label: null,
  note: null,
  checked: false,
};

export async function probeCapabilities(): Promise<Capabilities> {
  const support = await negotiate(1280, 720, 30);
  return {
    video: Boolean(support.video),
    audio: Boolean(support.audio),
    label: support.video?.label ?? null,
    note: supportNote(support),
    checked: true,
  };
}

/** Kept as a named export so callers read intent rather than a field. */
export const exportBlockedReason = (caps: Capabilities): string | null =>
  caps.checked ? caps.note : null;
